import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from "@ngrx/store";
import * as fromApp from "../../store/app.reducer";
import { Subscription } from "rxjs";
import { CustomerModel } from "../../models/customer.model";
import { InviteCoworkersModalComponent } from '../../shared/components/invite-coworkers-modal/invite-coworkers-modal.component';
import { ChangePasswordModalComponent } from '../../shared/components/change-password-modal/change-password-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { EmployeeModel } from '../../models/employee.model';
import { PusherService } from '../../service/pusher.service';
import { MsalService } from '@azure/msal-angular';
import * as AuthActions from '../../auth/store/auth.actions';
import { CreateCompanyModalComponent } from '../../shared/create-company-modal/create-company-modal.component';
import { EmployeeRoleModel } from '../../models/employeeRole.model';
import { PaginationModel } from '../../models/pagination.model';
import * as CompanyActions from './store/company.actions';
import { PhoneFormatService } from 'src/app/service/phone-format.service';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { ImageCropModalComponent } from './image-crop-modal/image-crop-modal.component';
import { PosthogService } from '../../service/posthog.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-company',
    templateUrl: './company.component.html',
    styleUrls: ['./company.component.scss'],
    // standalone: true,
    standalone: false
})
export class CompanyComponent implements OnInit, OnDestroy {
    public employee: EmployeeModel;
    public customer: CustomerModel;
    public subscription: Subscription;
    public allRoles: EmployeeRoleModel[];
    public users: EmployeeModel[];
    public paginatedUsers: EmployeeModel[] = [];
    public pagination: PaginationModel;
    public deleteUserModal: boolean = false;
    public userToDelete: EmployeeModel;
    public selectedFile: File | null = null;
    public previewUrl: string | null = null;
    selectedCompanyLogoFile: File | null = null;
    previewCompanyLogoUrl: string | null = null;
    public isEditingPhone = false;
    public editedPhone: string = '';
    public isEditingFirstName = false;
    public editedFirstName: string = '';
    public expandedJobplaces: Set<number> = new Set(); // Track which users have expanded jobplaces
    imageChangedEvent: any = null;
    showCropper = false;
    croppedImage: string | null = null;
    public isSsoUser: boolean = false;

    public page: number = 1;
    public per_page: number = 10;
    public paginate: number = 1;
    public pageSize: number = 5;
    public pageSizeOptions: number[] = [10, 20, 50, 100, 150, 200];

    constructor(
        private store: Store<fromApp.AppState>,
        private dialog: MatDialog,
        private pusherService: PusherService,
        private msalService: MsalService,
        private phoneFormatService: PhoneFormatService,
        private posthogService: PosthogService,
        private router: Router,
        private translate: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.subscription = this.store.subscribe(appState => {
            this.customer = appState.auth.customer;
            this.employee = appState.auth.employee;
            this.users = appState.company.employees.filter(user => user.id != this.employee.id);
            this.pagination = appState.company.pagination;
            this.allRoles = appState.shared.employeeRoles;
            this.applyPagination();
        });

        // Check if user logged in via Google or Microsoft SSO
        const authProvider = localStorage.getItem('authProvider');
        const hasMsalAccount = !!this.msalService.instance.getActiveAccount();
        this.isSsoUser = authProvider === 'google' || authProvider === 'microsoft' || hasMsalAccount;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    onInviteCoworkers() {
        this.dialog.open(InviteCoworkersModalComponent, {
            data: {
                employee: this.employee,
                customer: this.customer
            }
        });
    }

    public onOpenPasswordModal(): void {
        const dialogRef = this.dialog.open(ChangePasswordModalComponent, {
            data: {
                employee: this.employee
            }
        });
    }

    public onChangeApplicationLanguage(language: string): void {
        this.translate.use(language);
        localStorage.setItem('locale', language);
        this.store.dispatch(new AuthActions.UpdateProfile({
            employeeId: this?.employee?.id,
            locale: language
        }));
    }

    public onOpenCreateCompanyModal(): void {
        this.dialog.open(CreateCompanyModalComponent, {
            data: {
                employee: this.employee
            }
        });
    }

    public onDeleteUser(user: EmployeeModel): void {
        this.deleteUserModal = true;
        this.userToDelete = user;
    }

    public onSubmitDelete(): void {
        this.deleteUserModal = false;
        this.store.dispatch(new CompanyActions.DeleteEmployee({
            customerId: this.customer.id,
            employeeId: this?.userToDelete?.id
        }));
        this.userToDelete = null;

        this.store.dispatch(new CompanyActions.LoadEmployees({
            customerId: this.customer.id,
        }));
        // Reset to first page if current page becomes empty after deletion
        setTimeout(() => {
            if (this.users && this.users.length > 0) {
                const maxPage = Math.ceil(this.users.length / this.per_page);
                if (this.page > maxPage) {
                    this.page = 1;
                }
            }
            this.applyPagination();
        }, 100);
    }

    public async onLogout(): Promise<void> {
        try {
            // 1. First disconnect realtime services and dispatch logout
            this.pusherService.disconnect();
            this.posthogService.reset(); // Reset PostHog user identity
            this.store.dispatch(new AuthActions.Logout());

            // 2. Clear all storage
            sessionStorage.clear();
            localStorage.clear();

            // 3. Clear MSAL state if exists
            if (this.msalService.instance.getActiveAccount()) {
                this.msalService.instance.setActiveAccount(null);
            }

            // 4. Clean up service worker and caches in the background
            Promise.all([
                // Unregister service workers
                navigator.serviceWorker?.getRegistrations()
                    .catch(err => console.error('SW cleanup error:', err)),

                // Clear caches
                'caches' in window ?
                    caches.keys()
                        .then(cacheNames => Promise.all(cacheNames.map(name => caches.delete(name))))
                        .catch(err => console.error('Cache cleanup error:', err))
                    : Promise.resolve()
            ]).catch(err => console.error('Cleanup error:', err));

            // 5. Hard redirect to ensure clean slate
            this.router.navigate(['/components/assignments']);
        } catch (error) {
            console.error('Error during logout:', error);
            this.router.navigate(['/components/assignments']);
        }
    }

    onChangeAvatar(event: any) {
        const file = event.target.files[0];
        if (!file) return;

        const dialogRef = this.dialog.open(ImageCropModalComponent, {
            width: '600px',
            data: { imageChangedEvent: event }
        });

        dialogRef.afterClosed().subscribe((croppedImage: string) => {
            if (croppedImage) {
                this.previewUrl = croppedImage;
            }
        });
    }

    public onSaveAvatar(): void {
        if (this.previewUrl) {
            const file = this.base64ToFile(this.previewUrl, 'avatar.png');

            this.store.dispatch(new AuthActions.UpdateProfile({
                employeeId: this.employee.id,
                avatar: file
            }));

            // Reset preview
            this.previewUrl = null;

            // Reload employees list
            this.store.dispatch(new CompanyActions.LoadEmployees({
                customerId: this.customer.id,
            }));
        }
    }

    private base64ToFile(base64: string, filename: string): File {
        const arr = base64.split(',');
        const mime = arr[0].match(/:(.*?);/)![1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    public onChangeLogo(): void {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/jpeg,image/png,image/jpg,image/webp';
        fileInput.onchange = (event: any) => {
            if (event.target.files.length > 0) {
                const file = event.target.files[0];
                if (file) {
                    this.selectedCompanyLogoFile = file;
                    const reader = new FileReader();
                    reader.onload = () => {
                        this.previewCompanyLogoUrl = reader.result as string;
                        this.onSaveCompanyLogo();
                    };
                    reader.readAsDataURL(file);
                }
            }
        };
        fileInput.click();
    }

    public onSaveCompanyLogo(): void {
        if (this.selectedCompanyLogoFile) {
            this.store.dispatch(new CompanyActions.UpdateCustomerProfile({
                customerId: this.customer.id,
                image: this.selectedCompanyLogoFile
            }));
            this.selectedCompanyLogoFile = null;
            this.previewCompanyLogoUrl = null;
            this.store.dispatch(new AuthActions.Autologin());
        }
    }

    onImageError(event: Event): void {
        const imgElement = event.target as HTMLImageElement;
        imgElement.src = '../../../assets/img/safetly_logo_re.png';
    }

    //Name edit functionality
    public onEditFirstName(): void {
        this.isEditingFirstName = true;
        // If employee has a name, prefill it; otherwise leave empty
        this.editedFirstName = this.employee?.firstName ? this.employee.firstName : null;
    }

    public onSaveFirstName(): void {
        const trimmedName = this.editedFirstName.trim();

        this.store.dispatch(new AuthActions.UpdateProfile({
            employeeId: this?.employee?.id,
            firstName: trimmedName || null
        }));
        this.isEditingFirstName = false;
    }

    // phone edit functionality
    onEditPhone(): void {
        this.isEditingPhone = true;
        this.editedPhone = this.employee?.phone || null;
    }

    onSavePhone(): void {
        // Dispatch update or call backend
        this.store.dispatch(new AuthActions.UpdateProfile({
            employeeId: this?.employee?.id,
            phone: this.editedPhone || null
        }));
        this.isEditingPhone = false;
    }

    onPhoneKeyPress(event: KeyboardEvent): void {
        const allowedPattern = /[0-9]/;
        if (!allowedPattern.test(event.key)) {
            event.preventDefault();
        }
    }

    onPhoneInput(event: any): void {
        const input = event.target.value;
        const formatted = this.phoneFormatService.formatSwedishPhoneNumber(input);
        event.target.value = formatted;
    }

    // Helper methods for jobplace display with expandable functionality
    getVisibleJobplaces(jobplaces: any[], userId: number): any[] {
        if (!jobplaces || jobplaces.length <= 3) {
            return jobplaces || [];
        }
        // If expanded, show all jobplaces, otherwise show first 2
        return this.isJobplacesExpanded(userId) ? jobplaces : jobplaces.slice(0, 2);
    }

    getHiddenJobplacesCount(jobplaces: any[]): number {
        if (!jobplaces || jobplaces.length <= 3) {
            return 0;
        }
        return jobplaces.length - 2;
    }

    isJobplacesExpanded(userId: number): boolean {
        return this.expandedJobplaces.has(userId);
    }

    toggleJobplacesExpansion(userId: number): void {
        if (this.expandedJobplaces.has(userId)) {
            this.expandedJobplaces.delete(userId);
        } else {
            this.expandedJobplaces.add(userId);
        }
    }

    imageCropped(event: ImageCroppedEvent) {
        this.croppedImage = event.base64!;
    }

    applyCrop() {
        this.previewUrl = this.croppedImage; // show preview
        this.showCropper = false;
        this.selectedFile = null;
    }

    cancelCrop() {
        this.showCropper = false;
        this.selectedFile = null;
        this.croppedImage = '';
    }

    fileChangeEvent(event: any): void {
        if (event.target.files && event.target.files.length > 0) {
            this.selectedFile = event.target.files[0];
            this.imageChangedEvent = event;
            this.showCropper = true;
        }
    }

    getToggleText(jobplaces: any[], userId: number): string {
        if (!jobplaces || jobplaces.length <= 3) {
            return '';
        }
        return this.isJobplacesExpanded(userId) ? 'Visa färre' : `+${this.getHiddenJobplacesCount(jobplaces)} Visa mer`;
    }

    onBack(): void {
        this.router.navigate(['/components/assignments']);
    }

    // Pagination function
    applyPagination(): void {
        if (this.paginate === 1 && this.users && this.users.length > 5) {
            const startIndex = (this.page - 1) * this.per_page;
            const endIndex = startIndex + this.per_page;
            this.paginatedUsers = this.users.slice(startIndex, endIndex);
        } else {
            this.paginatedUsers = this.users || [];
        }
    }

    onPageChange(event: any): void {
        this.page = event.pageIndex + 1;
        this.per_page = event.pageSize;
        this.pageSize = event.pageSize;
        this.applyPagination();
    }

    get totalUsers(): number {
        return this.users ? this.users.length : 0;
    }
}